import OperationalLayout from "../../../components/OperationalLayout";
import ShrinkageCard from "../../../components/ShrinkageCard";
import { useSearchParams } from "react-router-dom";
import {
  useOptionShrinkage,
  useOptionFlop5,
  useOptionDeava,
  useOptionShrinkageDepartment,
} from "../../../utils/options";
import ShrinkageByRayonCard from "../../../components/ShrinkageByRayonCard";
import useSWR from "swr";
import { useStores } from "../../../context/storesContext";
import { fetcherWithStoreKey } from "../../../utils/fetcher";
import {
  CasseByRayon,
  DEAVA,
  Flop5,
  Shrinkage as ShrinkageType,
} from "../../../types/operational";
import Flop5Card from "../../../components/Flop5Card";
import DeavaCard from "../../../components/DeavaCard";
import { useTranslation } from "react-i18next";
import { useKpiVisibility } from "../../../kpis/useKpiVisibility";
import { useRange } from "../../../context/rangeContext";

function Shrinkage() {
  const { selectedStore } = useStores();
  const [searchParams] = useSearchParams();
  const optionShrinkage = useOptionShrinkage();
  const optionShrinkageDepartment = useOptionShrinkageDepartment();
  const optionFlop5 = useOptionFlop5();
  const optionDeava = useOptionDeava();
  const { getKpiRange } = useRange();

  const shrinkageStoreRange = searchParams.get("shrinkage") ?? getKpiRange('shrinkage') ?? optionShrinkage?.[0].value;
  const shrinkageDepartmentRange = searchParams.get("shrinkageDepartment") ?? getKpiRange('shrinkageDepartment') ?? optionShrinkageDepartment?.[0].value;
  const flopRange = searchParams.get("flopRange") ?? getKpiRange('flopRange') ?? optionFlop5?.[0].value;
  const deavaRange = searchParams.get("deavaRange") ?? getKpiRange('deavaRange') ?? optionDeava?.[0].value;

  //CASSE MAGASIN
  const { data: shrinkageStoreData, error: shrinkageStoreError } =
    useSWR<ShrinkageType>(
      () =>
        selectedStore?.storeKey && shrinkageStoreRange
          ? [
              `/operational/shrinkage?range=${shrinkageStoreRange}`,
              selectedStore?.storeKey as string,
            ]
          : null,
      ([url, storeEan]) =>
        fetcherWithStoreKey(url,  storeEan as string)
    );

  //CASSE RAYON
  const { data: shrinkageDepartmentData, error: shrinkageDepartmentError } =
    useSWR<CasseByRayon[]>(
      () =>
        selectedStore?.storeKey && shrinkageDepartmentRange
          ? [
              `/operational/shrinkageByDepartment?range=${shrinkageDepartmentRange}`,
              
              selectedStore?.storeKey as string,
            ]
          : null,
      ([url, storeEan]) =>
        fetcherWithStoreKey(url,  storeEan as string)
    );

  //FLOP
  const { data: flopData, error: flopError } = useSWR<Flop5[]>(
    () =>
      selectedStore?.storeKey && flopRange
        ? [
            `/operational/flop5?range=${flopRange}`,
            
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );

  //DEAVA
  const { data: deavaData, error: deavaError } = useSWR<DEAVA>(
    () =>
      selectedStore?.storeKey && deavaRange
        ? [
            `/operational/markdown?range=${deavaRange}`,
            
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );

  //TAUX De DONS
  const { data: donationData } = useSWR<{
    donationRate: number;
  }>(
    () =>
      selectedStore?.storeKey && shrinkageStoreRange
        ? [
            `/operational/donationRate?range=${shrinkageStoreRange}`,
            
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );

  const {t} = useTranslation();
  const {isKpiExists, isModuleExists} = useKpiVisibility();
  
  if (!isModuleExists("Operationnel")) {
    return null;
  }

  return (
    <OperationalLayout title={t('operationnel.demarque')}>
      {isKpiExists("Shrinkage", "Shrinkage") && (
        <ShrinkageCard
          title={t('operationnel.casse')}
          options={optionShrinkage}
          range={"shrinkage"}
          dons={donationData?.donationRate as number}
          shrinkageData={shrinkageStoreData as ShrinkageType}
          isLoading={!shrinkageStoreData}
          isUnavailable={!!shrinkageStoreError || shrinkageStoreData === null || donationData?.donationRate === null}
          openInformationModal={() => null}
        />
      )}
        {isKpiExists("Shrinkage", "ShrinkageByRayon") && (
          <ShrinkageByRayonCard
            title={t('operationnel.casse_par_rayon')}
            options={optionShrinkageDepartment}
            range={"shrinkageDepartment"}
            listValue={shrinkageDepartmentData as CasseByRayon[]}
            isLoading={!shrinkageDepartmentData}
            importedDateTime={shrinkageDepartmentData?.[0]?.importedDateTime}
            isUnavailable={ !!shrinkageDepartmentError || shrinkageDepartmentData === null || shrinkageDepartmentData?.[0]?.departmentKey === null}
            openInformationModal={() => null}
          />
        )}
        {isKpiExists("Shrinkage", "Flop5") && (
          <Flop5Card
            title= {`${t('operationnel.flop')} 5`}
            options={optionFlop5}
            range={"flopRange"}
            listValue={flopData as Flop5[]}
            isLoading={!flopData}
            isUnavailable={!!flopError || flopData === null || flopData?.[0]?.itemDesc === null}
            openInformationModal={() => null}
          />
        )}
      {isKpiExists("Shrinkage", "Deava") && (
        <DeavaCard
          title={t('operationnel.deava')}
          options={optionDeava}
          range={"deavaRange"}
          deavaData={deavaData as DEAVA}
          isLoading={!deavaData}
          isUnavailable={!!deavaError || deavaData === null || deavaData?.totalInitializedItemCount === null}
          openInformationModal={() => null}
        />
      )}
    </OperationalLayout>
  );
}

export default Shrinkage;
